/**
 * Summary Component — renders KPI metric cards from the resolver-driven
 * `summary.metrics` payload. Falls back to a small placeholder set when no
 * indicator id is supplied (e.g. preview before save).
 */

import { Loader2 } from 'lucide-react';

import { useIndicatorData, type IndicatorSummaryMetric } from './useIndicatorData';

interface SummaryComponentProps {
  indicatorId?: number;
}

const FALLBACK_METRICS: IndicatorSummaryMetric[] = [
  { label: 'Total Customers', value: '0', change: '' },
  { label: 'Active Contracts', value: '0', change: '' },
  { label: 'Avg Score', value: '0', change: '' },
  { label: 'Approved', value: '0', change: '' },
];

function changeClass(change: string): string {
  if (change.startsWith('+')) return 'text-green-600';
  if (change.startsWith('-')) return 'text-red-600';
  return 'text-gray-600';
}

export function SummaryComponent({ indicatorId }: SummaryComponentProps) {
  const { data, loading } = useIndicatorData(indicatorId);

  if (indicatorId && loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const metrics = data?.summary?.metrics?.length ? data.summary.metrics : FALLBACK_METRICS;

  return (
    <div className="grid grid-cols-2 gap-4 p-2">
      {metrics.map((metric, index) => (
        <div key={index} className="text-center p-3 bg-muted/50 rounded-lg">
          <div className="text-lg font-bold">{metric.value}</div>
          <div className="text-xs text-muted-foreground">{metric.label}</div>
          {metric.change ? (
            <div className={`text-xs ${changeClass(metric.change)}`}>{metric.change}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
